<script setup>
import { ref, computed, watch, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { isValidDomain } from '@chatwoot/utils';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import CompanyAPI from 'dashboard/api/companies';

const props = defineProps({
  company: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'close']);

const { t } = useI18n();

const dialogRef = ref(null);
const integrations = ref({
  hubspot: false,
  teamleader: false,
});

const formData = reactive({
  name: '',
  domain: '',
  description: '',
  vatNumber: '',
  hubspotId: '',
  teamleaderId: '',
});

const rules = {
  domain: {
    isValidDomain: helpers.withMessage(
      () => t('COMPANIES.FORM.VALIDATION.INVALID_DOMAIN'),
      value => !value || isValidDomain(value)
    ),
  },
};

const v$ = useVuelidate(rules, formData);

const isEditMode = computed(() => !!props.company);

const dialogTitle = computed(() =>
  isEditMode.value
    ? t('COMPANIES.FORM.EDIT_TITLE')
    : t('COMPANIES.FORM.CREATE_TITLE')
);

const confirmButtonLabel = computed(() =>
  isEditMode.value ? t('COMPANIES.FORM.UPDATE') : t('COMPANIES.FORM.CREATE')
);

const isFormValid = computed(() => {
  return formData.name.trim().length > 0;
});

// Fetch integrations on mount
onMounted(async () => {
  try {
    const response = await CompanyAPI.getIntegrations();
    integrations.value = response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch integrations:', error);
  }
});

// Watch for company prop changes to populate form in edit mode
watch(
  () => props.company,
  newCompany => {
    if (newCompany) {
      Object.assign(formData, {
        name: newCompany.name || '',
        domain: newCompany.domain || '',
        description: newCompany.description || '',
        vatNumber: newCompany.vatNumber || '',
        hubspotId: newCompany.hubspotId || '',
        teamleaderId: newCompany.teamleaderId || '',
      });
      v$.value.$reset();
    }
  },
  { immediate: true }
);

const open = () => {
  if (!props.company) {
    // Reset form for create mode
    Object.assign(formData, {
      name: '',
      domain: '',
      description: '',
      vatNumber: '',
      hubspotId: '',
      teamleaderId: '',
    });
    v$.value.$reset();
  }
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleDialogClose = () => {
  emit('close');
};

const handleSave = async () => {
  if (!isFormValid.value) return;

  // Validate form
  const isFormCorrect = await v$.value.$validate();
  if (!isFormCorrect) return;

  const payload = {
    name: formData.name.trim(),
    domain: formData.domain.trim(),
    description: formData.description.trim(),
    vat_number: formData.vatNumber.trim(),
    hubspot_id: formData.hubspotId.trim(),
    teamleader_id: formData.teamleaderId.trim(),
  };

  // Remove empty fields
  Object.keys(payload).forEach(key => {
    if (payload[key] === '') {
      delete payload[key];
    }
  });

  if (isEditMode.value) {
    payload.id = props.company.id;
  }

  emit('save', payload);
};

defineExpose({
  open,
  close,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="dialogTitle"
    :confirm-button-label="confirmButtonLabel"
    :cancel-button-label="t('COMPANIES.FORM.CANCEL')"
    :disable-confirm-button="!isFormValid"
    :is-loading="isLoading"
    width="lg"
    @confirm="handleSave"
    @close="handleDialogClose"
  >
    <form class="flex flex-col gap-4 p-1" @submit.prevent="handleSave">
      <Input
        v-model="formData.domain"
        :label="t('COMPANIES.FORM.FIELDS.DOMAIN')"
        :placeholder="t('COMPANIES.FORM.PLACEHOLDERS.DOMAIN')"
        :message="v$.domain.$error ? v$.domain.$errors[0].$message : ''"
        :message-type="v$.domain.$error ? 'error' : 'info'"
        autofocus
        @blur="v$.domain.$touch()"
      />

      <Input
        v-model="formData.name"
        :label="t('COMPANIES.FORM.FIELDS.NAME')"
        :placeholder="t('COMPANIES.FORM.PLACEHOLDERS.NAME')"
        required
      />

      <Textarea
        v-model="formData.description"
        :label="t('COMPANIES.FORM.FIELDS.DESCRIPTION')"
        :placeholder="t('COMPANIES.FORM.PLACEHOLDERS.DESCRIPTION')"
        :rows="3"
      />

      <Input
        v-model="formData.vatNumber"
        :label="t('COMPANIES.FORM.FIELDS.VAT_NUMBER')"
        :placeholder="t('COMPANIES.FORM.PLACEHOLDERS.VAT_NUMBER')"
      />

      <Input
        v-if="integrations.hubspot"
        v-model="formData.hubspotId"
        :label="t('COMPANIES.FORM.FIELDS.HUBSPOT_ID')"
        :placeholder="t('COMPANIES.FORM.PLACEHOLDERS.HUBSPOT_ID')"
      />

      <Input
        v-if="integrations.teamleader"
        v-model="formData.teamleaderId"
        :label="t('COMPANIES.FORM.FIELDS.TEAMLEADER_ID')"
        :placeholder="t('COMPANIES.FORM.PLACEHOLDERS.TEAMLEADER_ID')"
      />
    </form>
  </Dialog>
</template>
